import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { TrendingUp, Activity, Thermometer, Droplets, Battery } from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';

const RANGES = [
  { label: 'Live',  count: 30 },
  { label: '1 hr',  count: 60 },
  { label: 'All',   count: 0  }
];

const METRICS = [
  { key: 'temperature', label: 'Temp',    icon: Thermometer, color: '#ef4444', unit: '°C' },
  { key: 'humidity',    label: 'Humidity',icon: Droplets,    color: '#3b82f6', unit: '%'  },
  { key: 'vibration',   label: 'Vibrate', icon: Activity,    color: '#ec4899', unit: 'm/s²' },
  { key: 'battery',     label: 'Battery', icon: Battery,     color: '#10b981', unit: '%'  }
];

export default function AnalyticsPage() {
  const { history } = useMQTT();
  const [rangeIdx, setRangeIdx] = useState(0);
  const [metricKey, setMetricKey] = useState('temperature');

  const data = useMemo(() => {
    const slice = RANGES[rangeIdx].count
      ? history.slice(-RANGES[rangeIdx].count)
      : history;
    return slice.map(h => ({
      time: new Date(h.timestamp).toLocaleTimeString('en-US',
        { hour: '2-digit', minute: '2-digit' }),
      temperature: Number((h.temperature ?? 0).toFixed(1)),
      humidity:    Number((h.humidity    ?? 0).toFixed(1)),
      vibration:   Number((h.vibration   ?? 0).toFixed(2)),
      battery:     Number((h.battery     ?? 0).toFixed(0))
    }));
  }, [history, rangeIdx]);

  const metric = METRICS.find(m => m.key === metricKey);

  const stats = useMemo(() => {
    const vals = data.map(d => d[metricKey]).filter(Number.isFinite);
    if (!vals.length) return { min: 0, max: 0, avg: 0 };
    const sum = vals.reduce((a, b) => a + b, 0);
    return { min: Math.min(...vals), max: Math.max(...vals), avg: sum / vals.length };
  }, [data, metricKey]);

  return (
    <div className="grid gap-4 sm:gap-5">
      {/* Range tabs */}
      <motion.div layout className="card-surface p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center text-white">
            <TrendingUp className="w-4 h-4" />
          </span>
          <p className="font-bold">Analytics</p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-full bg-black/[0.04] dark:bg-white/[0.05]">
          {RANGES.map((r, i) => (
            <button
              key={r.label}
              onClick={() => setRangeIdx(i)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                rangeIdx === i
                  ? 'bg-white dark:bg-slate-700 shadow text-indigo-600 dark:text-indigo-300'
                  : 'opacity-70'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Metric swipe row */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 snap-x">
        {METRICS.map(m => {
          const Icon = m.icon;
          const active = m.key === metricKey;
          return (
            <motion.button
              key={m.key}
              whileTap={{ scale: 0.95 }}
              onClick={() => setMetricKey(m.key)}
              className={`snap-start shrink-0 px-4 py-2.5 rounded-2xl border transition flex items-center gap-2 ${
                active
                  ? 'bg-white dark:bg-slate-800 border-indigo-500/40 shadow-card'
                  : 'bg-transparent border-transparent opacity-70'
              }`}
              style={active ? { color: m.color } : undefined}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-bold">{m.label}</span>
            </motion.button>
          );
        })}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        {['min', 'avg', 'max'].map(k => (
          <div key={k} className="card-surface p-3 text-center">
            <p className="text-[10px] uppercase tracking-widest opacity-60 font-bold">{k}</p>
            <p className="text-xl sm:text-2xl font-extrabold tabular-nums mt-1">
              {stats[k].toFixed(2)}
            </p>
            <p className="text-[10px] opacity-60">{metric.unit}</p>
          </div>
        ))}
      </div>

      {/* Main chart */}
      <motion.div layout className="card-surface p-4 sm:p-5">
        <p className="font-bold mb-3">{metric.label} trend</p>
        <div className="h-[260px] sm:h-[320px] -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="metricGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={metric.color} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={metric.color} stopOpacity={0}   />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(127,127,127,0.18)" />
              <XAxis dataKey="time" stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <YAxis stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.92)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  color: 'white'
                }}
              />
              <Area
                type="monotone"
                dataKey={metricKey}
                stroke={metric.color}
                strokeWidth={2.5}
                fill="url(#metricGrad)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Multi compare */}
      <motion.div layout className="card-surface p-4 sm:p-5">
        <p className="font-bold mb-3">Temperature vs Humidity</p>
        <div className="h-[220px] -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(127,127,127,0.18)" />
              <XAxis dataKey="time" stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <YAxis stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.92)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  color: 'white'
                }}
              />
              <Line type="monotone" dataKey="temperature" stroke="#ef4444" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="humidity"    stroke="#3b82f6" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <motion.div layout className="card-surface p-4 sm:p-5">
        <p className="font-bold mb-3">Vibration distribution</p>
        <div className="h-[180px] -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(127,127,127,0.18)" />
              <XAxis dataKey="time" stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <YAxis stroke="currentColor" opacity="0.4" tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.92)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 12,
                  color: 'white'
                }}
              />
              <Bar dataKey="vibration" fill="#ec4899" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>
    </div>
  );
}
